import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl } from 'react-intl'

import { LOCALE_PREFIX } from './constants'

import FixedColumn from './components/FixedColumn'
import DataColumns from './components/DataColumns'
import ResultsColumn from './components/ResultsColumn'

import hocConnect from './hocConnect'
import './style.scss'

@hocConnect
@injectIntl
export default class FinanceTable extends Component {
    static propTypes = {
        addOpenRow: PropTypes.func.isRequired,
        changeCompareYear: PropTypes.func.isRequired,
        changeCurrentStandard: PropTypes.func,
        changeSampleYear: PropTypes.func.isRequired,
        changeYearBounds: PropTypes.func.isRequired,
        compareYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        // Currently selected year to compare
        currentStandard: PropTypes.string,
        /*
            Array of objects, each object is a table column
            Rows of a column - property [values]
            values: {
                [columnCode]: value - row value, columnCodes per sectionsDefinition
                ...
            }
            Can also contain: startDate (obj), endDate (obj), audit (bool), ban (bool), consolidation (bool)
        */
        data: PropTypes.array,
        decimalPlaces: PropTypes.number,
        id: PropTypes.string.isRequired,
        // Table ID, required (for reducer)
        intl: PropTypes.object.isRequired,
        isAdvanced: PropTypes.bool.isRequired,
        // Level of block content, if extended set isAdvanced: true, keep this false for finance analysis
        lowerBound: PropTypes.number,
        numberMagnitude: PropTypes.number,
        // Lower visible years slice bound
        openRows: PropTypes.array,
        removeOpenRows: PropTypes.func.isRequired,
        // Currently selected sample year
        sampleYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        // Definition of table sections and rows, required
        sectionsDefinition: PropTypes.arrayOf(
            PropTypes.shape({
                title: PropTypes.string.isRequired,
                rows: PropTypes.arrayOf(
                    PropTypes.shape({
                        // String shown before title
                        prefix: PropTypes.string,
                        // Highlighted rows have bold font weight and grey background
                        highlight: PropTypes.bool,
                        // Text content of the row
                        text: PropTypes.string.isRequired,
                        // Accessor being used to select data
                        columnCode: PropTypes.string.isRequired,
                        // Level of the row - sets left indentation
                        indentationLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                        configuration: PropTypes.shape({
                            unit: PropTypes.string,
                            higherIsBetter: PropTypes.string,
                        }),
                    })
                ).isRequired,
            })
        ),
        setOpenRows: PropTypes.func.isRequired,
        // Sets lower and upper bound for visible years slice
        setYearBounds: PropTypes.func.isRequired,
        // Sets compare and sample years
        setYears: PropTypes.func.isRequired,
        // Disable/enable tooltips in FixedColumn
        // All tooltip texts must be passed through translates
        showRowTooltips: PropTypes.bool,
        // Show units and currency in column headers
        showUnitsAndCurr: PropTypes.bool,
        standards: PropTypes.array,
        // Table type: Results || Cashflow || Analysis (for sagas)
        tableType: PropTypes.string.isRequired,
        // Table title
        title: PropTypes.string,
        // Upper visible years slice bound
        upperBound: PropTypes.number,
    }

    static defaultProps = {
        showUnitsAndCurr: false,
        showRowTooltips: true,
        decimalPlaces: 2,
        numberMagnitude: 1,
    }

    constructor(props) {
        super(props)

        this.financeTable = React.createRef()

        this.state = {
            activeRowIndex: null,
        }
    }

    // Highlight row in every column on hover
    handleRowHover = (index) => {
        this.setState({ activeRowIndex: index })
    }

    handleMouseLeaveRow = () => {
        this.setState({ activeRowIndex: null })
    }

    render() {
        const {
            id,
            title,
            data,
            currentStandard,
            sectionsDefinition,
            isAdvanced,
            standards,
            intl,
            compareYear,
            sampleYear,
            setYearBounds,
            changeYearBounds,
            upperBound,
            lowerBound,
            changeCompareYear,
            changeSampleYear,
            setYears,
            setOpenRows,
            decimalPlaces,
            showUnitsAndCurr,
            showRowTooltips,
            changeCurrentStandard,
            openRows,
            tableType,
            addOpenRow,
            removeOpenRows,
            numberMagnitude,
        } = this.props
        const { activeRowIndex } = this.state

        return (
            <div className={'finance-table-container'} ref={this.financeTable}>
                <div className={'table-title'}>
                    <span>{title || ''}</span>
                </div>
                <div className={'table-container'}>
                    <FixedColumn
                        id={id}
                        data={data}
                        addOpenRow={addOpenRow}
                        removeOpenRows={removeOpenRows}
                        activeRowIndex={activeRowIndex}
                        currentStandard={currentStandard}
                        isAdvanced={isAdvanced}
                        handleRowHover={this.handleRowHover}
                        handleMouseLeaveRow={this.handleMouseLeaveRow}
                        handleCurrentStandardChange={changeCurrentStandard}
                        sectionsDefinition={sectionsDefinition}
                        standards={standards}
                        showRowTooltips={showRowTooltips}
                        openRows={openRows}
                        setOpenRows={setOpenRows}
                        tableType={tableType}
                        translates={{
                            exportAsImage: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.exportAsImage` })}`,
                            standardsText: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.standardsText` })}`,
                            standardsDropdownText: `${intl.formatMessage({
                                id: `${LOCALE_PREFIX}.standardsDropdownText`,
                            })}`,
                            expandAll: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.expandAll` })}`,
                            collapseAll: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.collapseAll` })}`,
                            emptySections: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.emptySections` })}`,
                            noStandardSelected: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.noStandardSelected` })}`,
                            availableYears: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.availableYears` })}`,
                            units: {
                                days: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.units.days` })}`,
                                ratio: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.units.ratio` })}`,
                                percent: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.units.percent` })}`,
                            },
                            tooltips: {
                                fi1008: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1008` })}`,
                                fi1009: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1009` })}`,
                                fi1016: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1016` })}`,
                                fi1004: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1004` })}`,
                                fi1005: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1005` })}`,
                                fi1006: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1006` })}`,
                                fi1001: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1001` })}`,
                                fi1002: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1002` })}`,
                                fi1018: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1018` })}`,
                                fi1021: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1021` })}`,
                                fi1023: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1023` })}`,
                                fi1024: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1024` })}`,
                                fi1025: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.tooltip.fi1025` })}`,
                            },
                        }}
                    />
                    <DataColumns
                        id={id}
                        activeRowIndex={activeRowIndex}
                        data={data}
                        decimalPlaces={decimalPlaces}
                        lowerBound={lowerBound}
                        upperBound={upperBound}
                        setYearBounds={setYearBounds}
                        changeYearBounds={changeYearBounds}
                        compareYear={compareYear}
                        isAdvanced={isAdvanced}
                        handleCompareYearChange={changeCompareYear}
                        handleRowHover={this.handleRowHover}
                        handleMouseLeaveRow={this.handleMouseLeaveRow}
                        handleSampleYearChange={changeSampleYear}
                        numberMagnitude={numberMagnitude}
                        sampleYear={sampleYear}
                        showUnitsAndCurr={showUnitsAndCurr}
                        standards={standards}
                        setYears={setYears}
                        sectionsDefinition={sectionsDefinition}
                        openRows={openRows}
                        tableType={tableType}
                        translates={{
                            asCompareYear: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.asCompareYear` })}`,
                            asSampleYear: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.asSampleYear` })}`,
                            dateFrom: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.date.from` })}`,
                            dateTo: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.date.to` })}`,
                            standard: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.standard` })}`,
                            sampleYear: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.sampleYear` })}`,
                            compareYear: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.compareYear` })}`,
                            audit: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.audit` })}`,
                            consolidation: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.consolidation` })}`,
                            thousand: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.thousand` })}`,
                            million: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.million` })}`,
                            billion: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.billion` })}`,
                            trillion: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.trillion` })}`,
                            CZK: `${intl.formatMessage({ id: `global.currency.CZK` })}`,
                            EUR: `${intl.formatMessage({ id: `global.currency.EUR` })}`,
                            yes: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.yes` })}`,
                            no: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.no` })}`,
                            noData: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.noData` })}`,
                            subjects: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.subjects` })}`,
                        }}
                    />
                    <ResultsColumn
                        id={id}
                        data={data}
                        activeRowIndex={activeRowIndex}
                        compareYear={compareYear}
                        isAdvanced={isAdvanced}
                        handleCompareYearChange={changeCompareYear}
                        handleRowHover={this.handleRowHover}
                        handleMouseLeaveRow={this.handleMouseLeaveRow}
                        handleSampleYearChange={changeSampleYear}
                        sampleYear={sampleYear}
                        sectionsDefinition={sectionsDefinition}
                        setYears={setYears}
                        openRows={openRows}
                        tableType={tableType}
                        translates={{
                            compareYear: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.compareYear` })}`,
                            sampleYear: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.sampleYear` })}`,
                            yearDevelopment: `${intl.formatMessage({ id: `${LOCALE_PREFIX}.yearDevelopment` })}`,
                        }}
                    />
                </div>
            </div>
        )
    }
}
